import React, { useEffect, useId } from 'react';
import { useFocusTrap } from '../hooks/useFocusTrap';

export interface ConfirmOptions {
  /** A question that names the thing: "Delete Sam?". */
  title: string;
  /** What will happen, in a sentence or two. */
  message?: React.ReactNode;
  /** The action, as a verb that says what it does: "Delete user", never "OK" or "Yes". */
  confirmLabel: string;
  cancelLabel?: string;
  /** danger: the action cannot be undone, so Cancel has the focus. */
  tone?: 'danger' | 'default';
}

interface Props extends ConfirmOptions {
  onConfirm: () => void;
  onCancel: () => void;
}

// A question that has to be answered before the page goes on. An alert dialog (it interrupts), kept inside the app
// instead of the browser's own box: it says what will happen, the action carries its name, Cancel is always there
// and has the focus when the action cannot be undone, Escape and a click outside both cancel, and Tab stays inside.
export default function ConfirmDialog({ title, message, confirmLabel, cancelLabel = 'Cancel', tone = 'danger', onConfirm, onCancel }: Props) {
  const id = useId();
  const dialogRef = useFocusTrap<HTMLDivElement>(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };
    // In the capture phase, so Escape answers this question and does not also close the panel behind it.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const danger = tone === 'danger';
  return (
    <div className="fixed inset-0 z-[1000005] flex items-center justify-center p-4">
      <div role="presentation" className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={message ? `${id}-message` : undefined}
        className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-gray-900/5 focus:outline-none"
      >
        <h2 id={`${id}-title`} className="text-base font-semibold text-gray-900">
          {title}
        </h2>
        {message && (
          <div id={`${id}-message`} className="mt-1.5 text-sm leading-relaxed text-gray-600">
            {message}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            data-autofocus={danger ? '' : undefined}
            onClick={onCancel}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 motion-reduce:transition-none"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            data-autofocus={danger ? undefined : ''}
            onClick={onConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none ${
              danger ? 'bg-red-700 hover:bg-red-800 focus-visible:ring-red-600' : 'bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
